import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { createCoachee } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { UserPlus, Loader2, ArrowLeft, User, Mail, Phone, Calendar, Briefcase, Target, MessageSquare } from 'lucide-react';

export default function CoacheeCreate() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    telefono: '',
    fecha_nacimiento: '',
    info_laboral: '',
    objetivos_generales: '',
    observaciones: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.nombre.trim()) {
      toast.error('El nombre es requerido');
      return;
    }

    setLoading(true);
    try {
      const newCoachee = await createCoachee(formData);
      toast.success('Coachee creado exitosamente');
      navigate(`/coachees/${newCoachee.id_coachee}`);
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (typeof detail === 'string') {
        toast.error(detail);
      } else if (Array.isArray(detail)) {
        toast.error(detail.map(e => e.msg || e.message || 'Error').join(', '));
      } else {
        toast.error('Error al crear coachee');
      }
    } finally {
      setLoading(false);
    }
  };

  const formFields = [
    { name: 'nombre', label: 'Nombre Completo', icon: User, type: 'text', placeholder: 'Juan Pérez', required: true },
    { name: 'email', label: 'Correo Electrónico', icon: Mail, type: 'email', placeholder: 'juan@email.com' },
    { name: 'telefono', label: 'Teléfono', icon: Phone, type: 'tel', placeholder: '+52 555 123 4567' },
    { name: 'fecha_nacimiento', label: 'Fecha de Nacimiento', icon: Calendar, type: 'date' },
    { name: 'info_laboral', label: 'Información Laboral', icon: Briefcase, type: 'text', placeholder: 'Gerente de Marketing en Empresa XYZ' },
  ];

  return (
    <Layout>
      <div className="max-w-3xl mx-auto space-y-6" data-testid="coachee-create-page">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-slate-100 rounded-full"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-heading text-3xl text-slate-800">
              Nuevo Coachee
            </h1>
            <p className="text-slate-500 mt-1">
              Registra la información de tu nuevo coachee
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Info Card */}
          <Card className="glass-card">
            <CardHeader className="border-b border-slate-100">
              <CardTitle className="text-heading text-lg flex items-center gap-2">
                <User className="w-5 h-5 text-blue-900" />
                Información Personal
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {formFields.map((field) => (
                  <div key={field.name} className="space-y-2">
                    <Label htmlFor={field.name} className="flex items-center gap-2">
                      <field.icon className="w-4 h-4 text-slate-400" />
                      {field.label}
                      {field.required && <span className="text-red-500">*</span>}
                    </Label>
                    <Input
                      id={field.name}
                      name={field.name}
                      type={field.type}
                      value={formData[field.name]}
                      onChange={handleChange}
                      placeholder={field.placeholder}
                      className="glass-input"
                      data-testid={`coachee-${field.name}-input`}
                    />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Objectives Card */}
          <Card className="glass-card">
            <CardHeader className="border-b border-slate-100">
              <CardTitle className="text-heading text-lg flex items-center gap-2">
                <Target className="w-5 h-5 text-blue-900" />
                Objetivos y Observaciones
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              <div className="space-y-2">
                <Label htmlFor="objetivos_generales" className="flex items-center gap-2">
                  <Target className="w-4 h-4 text-slate-400" />
                  Objetivos Generales
                </Label>
                <Textarea
                  id="objetivos_generales"
                  name="objetivos_generales"
                  value={formData.objetivos_generales}
                  onChange={handleChange}
                  placeholder="Describe los objetivos principales del proceso de coaching..."
                  className="glass-input min-h-[100px]"
                  data-testid="coachee-objectives-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="observaciones" className="flex items-center gap-2">
                  <MessageSquare className="w-4 h-4 text-slate-400" />
                  Observaciones Iniciales
                </Label>
                <Textarea
                  id="observaciones"
                  name="observaciones"
                  value={formData.observaciones}
                  onChange={handleChange}
                  placeholder="Notas adicionales sobre el coachee..."
                  className="glass-input min-h-[100px]"
                  data-testid="coachee-observations-input"
                />
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex justify-end gap-4">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => navigate(-1)}
              className="btn-outline"
            >
              Cancelar
            </Button>
            <Button 
              type="submit" 
              className="btn-primary"
              disabled={loading}
              data-testid="create-coachee-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creando...
                </>
              ) : (
                <>
                  <UserPlus className="w-4 h-4 mr-2" />
                  Crear Coachee
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </Layout>
  );
}
